<!-- "What's new" popup: shows the latest entry of src/whatsnew.js once per
     browser (localStorage), a moment after the ERP has loaded, and again on
     demand (meeting.openWhatsNew(), from the call panel). -->
<template>
  <vs-popup :active.sync="open" title="What's new in XYLO" class="whats-new-popup">
    <div v-if="entry" class="wn">
      <div class="wn__hero">
        <div class="wn__hero-text">
          <span class="wn__kicker">{{ entry.kicker }} · {{ entry.date }}</span>
          <h2 class="wn__title">{{ entry.title }}</h2>
          <p class="wn__intro">{{ entry.intro }}</p>
        </div>
        <div class="wn__hero-art">
          <span class="wn__orbit"></span>
          <span class="wn__orbit wn__orbit--2"></span>
          <feather-icon icon="VideoIcon" svgClasses="w-9 h-9" />
        </div>
      </div>

      <ul class="wn__list">
        <li v-for="(f, i) in entry.features" :key="f.title" class="wn__item" :style="{ animationDelay: `${120 + (i * 70)}ms` }">
          <span class="wn__icon"><feather-icon :icon="f.icon" svgClasses="w-5 h-5" /></span>
          <div class="wn__body">
            <strong>{{ f.title }}</strong>
            <p>{{ f.text }}</p>
          </div>
        </li>
      </ul>

      <footer class="wn__footer">
        <vs-button type="border" @click="close">Got it</vs-button>
        <vs-button v-if="entry.cta" icon-pack="feather" icon="icon-settings" @click="cta">{{ entry.cta.label }}</vs-button>
      </footer>
    </div>
  </vs-popup>
</template>

<script>
import meeting from '@/services/meeting.js'
import whatsnew from '@/whatsnew.js'

const SEEN_KEY = 'whats-new-seen'
const AUTO_OPEN_DELAY_MS = 2500 // let the page settle (and the 2FA prompt, if any) first

export default {
  data () {
    return { entry: whatsnew[0] || null, timer: null }
  },
  computed: {
    open: {
      get () { return meeting.state.ui.whatsNew },
      set (v) { meeting.state.ui.whatsNew = v }
    }
  },
  watch: {
    open (val) { if (!val) this.markSeen() }
  },
  mounted () {
    if (!this.entry || this.seen()) return
    this.timer = setTimeout(() => { this.open = true }, AUTO_OPEN_DELAY_MS)
  },
  beforeDestroy () { clearTimeout(this.timer) },
  methods: {
    seen () {
      try { return localStorage.getItem(SEEN_KEY) === this.entry.id } catch (e) { return true }
    },
    markSeen () {
      try { localStorage.setItem(SEEN_KEY, this.entry.id) } catch (e) { /* private mode */ }
    },
    close () { this.open = false },
    cta () {
      this.close()
      if (this.entry.cta && this.entry.cta.action === 'deviceCheck') {
        this.$nextTick(() => meeting.openDeviceCheck())
      }
    }
  }
}
</script>

<style>
.whats-new-popup .vs-popup { width: 640px; max-width: calc(100vw - 24px); border-radius: 16px; overflow: hidden; }
.whats-new-popup .vs-popup--content { padding: 0; margin: 0; max-height: calc(100vh - 120px); overflow-x: hidden; }
</style>

<style scoped>
.wn { display: flex; flex-direction: column; }
.wn__hero {
  display: flex; align-items: center; justify-content: space-between; gap: 20px;
  padding: 28px 28px 24px;
  overflow: hidden; /* the pulsing orbits grow past the edge: never a scrollbar */
  background: linear-gradient(135deg, #7367f0 0%, #4e3fd6 55%, #28c76f 160%);
  color: #fff;
}
.wn__kicker {
  display: inline-block; font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
  background: rgba(255,255,255,.18); padding: 4px 10px; border-radius: 999px; margin-bottom: 12px;
}
.wn__title { color: #fff; font-size: 22px; line-height: 1.2; margin: 0 0 8px; font-weight: 700; }
.wn__intro { margin: 0; font-size: 14px; color: rgba(255,255,255,.85); }
.wn__hero-art {
  position: relative; flex-shrink: 0; width: 96px; height: 96px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,.14); color: #fff;
}
.wn__orbit {
  position: absolute; inset: -10px; border-radius: 50%; border: 1.5px solid rgba(255,255,255,.35);
  animation: wn-pulse 2.4s ease-out infinite;
}
.wn__orbit--2 { animation-delay: 1.2s; }
@keyframes wn-pulse {
  0% { transform: scale(.8); opacity: .9; }
  100% { transform: scale(1.35); opacity: 0; }
}
.wn__list { list-style: none; margin: 0; padding: 20px 28px 8px; display: flex; flex-direction: column; gap: 14px; }
.wn__item {
  display: flex; gap: 14px; align-items: flex-start;
  animation: wn-in .4s ease both;
}
@keyframes wn-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
.wn__icon {
  flex-shrink: 0; width: 40px; height: 40px; border-radius: 12px;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(115,103,240,.12); color: #7367f0;
}
.wn__body strong { display: block; font-size: 14px; color: #1e293b; margin-bottom: 2px; }
.wn__body p { margin: 0; font-size: 13px; line-height: 1.45; color: #64748b; }
.wn__footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 28px 24px; }
@media (max-width: 560px) {
  .wn__hero { flex-direction: column-reverse; align-items: flex-start; padding: 22px 20px; }
  .wn__list, .wn__footer { padding-left: 20px; padding-right: 20px; }
}
</style>
